import { FC, MouseEventHandler, ReactNode } from 'react';
import Paper from '@o2/ui/Paper';
import Stack from '@o2/ui/Stack';
import Typography from '@o2/ui/Typography';
import IconButton from '@o2/ui/IconButton';
import TrashBin from '@o2/icons/TrashBin';
import LinearProgress, { LinearProgressProps } from '@o2/ui/LinearProgress';
import { fileUploadWidgetTemplateId } from './FileUploadWidget';
import GenericIcon from './GenericIcon';

interface ItemProps {
  /**
   * Description shown under the filename.
   */
  description?: ReactNode;

  filename: ReactNode;

  /**
   * Defaults to a generic gray file.
   */
  icon?: ReactNode;

  /**
   * Provide this prop if you want the delete button to be rendered.
   */
  onRemove?: MouseEventHandler;

  /**
   * If set, a progress bar will be shown at the bottom of the paper.
   *
   * If a value for the `error` prop is also provided, `ProgressProps` will take precedence over it.
   */
  ProgressProps?: LinearProgressProps;

  /**
   * If set, the provided element will be rendered along with a red 100% progress bar at the bottom.
   */
  error?: ReactNode;
}

const Item: FC<ItemProps> = ({ description, error, filename, icon, onRemove, ProgressProps, ...other }) => {
  return (
    <Paper
      data-elm={`${fileUploadWidgetTemplateId}_003`}
      borderColor="gray"
      p={{
        xs: 2,
        md: 3,
      }}
      {...other}
    >
      <Stack alignItems="center" direction="row" spacing={2}>
        {icon ?? <GenericIcon />}
        <Stack flexGrow={1} justifyContent="center">
          <Typography fontWeight="medium">{filename}</Typography>
          {description}
        </Stack>
        {error}
        {onRemove && (
          <IconButton
            variant="squared"
            onClick={onRemove}
            sx={(theme) => ({
              border: `1px solid ${theme.palette.error.main}`,
            })}
            color="error"
          >
            <TrashBin />
          </IconButton>
        )}
      </Stack>
      {(error || ProgressProps) && (
        <Paper.Footer p={0} sx={{ borderBottom: 'none' }}>
          <LinearProgress color={error ? 'error' : undefined} value={error ? 100 : undefined} {...ProgressProps} />
        </Paper.Footer>
      )}
    </Paper>
  );
};

Item.displayName = 'FileUploadWidget.Item';

export default Item;
